import { Button, type ButtonProps } from '@mui/material'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import type Immutable from 'immutable'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type UserCartStore from '../../UserData/_stores/UserCartStore'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { CartCheckoutStripeWithCard } from '../../UserData/_actions/UserCartActions'
import { useNavigate } from 'react-router'
import { EditLoader } from '../../UIData/_actions/UIDataActions'
import { getMetaCheckoutEventId, queueMetaServerEvent, trackMetaBrowserEvent } from '../../Common/Tracking/MetaConversions'
import { queueOpenAIAdsServerEvent, trackOpenAIAdsPaymentInfoAdded } from '../../Common/Tracking/OpenAIAdsTracking'

type Props = ButtonProps & {
  orders: Immutable.Map<string, UserCartStore>
  confirmationUrl: string
  selectedCreditCard: number
  savePaymentMethod: boolean
  stripeUpdate?: number
  onErrorText: (error: string) => void
  onDisable: (disable: boolean) => void
}

export default function StripePaymentButton(props: Props) {
  const { orders, confirmationUrl, selectedCreditCard, savePaymentMethod, stripeUpdate, onErrorText, onDisable, ...otherProps } = props

  const dispatch = useAppDispatch()
  const [t] = useTranslation('cart')
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()

  // Trigger PaymentIntent update on stripeUpdate
  useEffect(() => {
    if (!elements) {
      return
    }

    dispatch(EditLoader('payment', true))

    elements.fetchUpdates().then(response => {
      dispatch(EditLoader('payment', false))

      if (response.error?.message) {
        props.onErrorText(response.error.message)
      }
    }).catch(reason => {
      dispatch(EditLoader('payment', false))
      console.log(reason)
    })
  }, [dispatch, props.stripeUpdate, elements, props.onErrorText])

  const payWithIntent = useCallback(() => {
    if (!stripe || !elements) {
      console.log('Stripe has not loaded yet', stripe, elements)
      return
    }

    props.onDisable(true)
    props.onErrorText('')

    props.orders.forEach(cart => {
      const eventId = getMetaCheckoutEventId('AddPaymentInfo', cart, 'stripe-new-card')
      trackMetaBrowserEvent('AddPaymentInfo', cart, eventId)
      queueMetaServerEvent('AddPaymentInfo', cart, eventId, 'stripe')
      trackOpenAIAdsPaymentInfoAdded(cart, eventId)
      queueOpenAIAdsServerEvent('payment_info_added', cart, eventId)
    })

    stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.SITE_URL + 'checkout/creditcard-v2/payment-intent-callback?saveCard=' + (props.savePaymentMethod ? '1' : '0') + '&redirect_confirm=' + props.confirmationUrl
      },
      redirect: 'always'
    }).then((result) => {
      props.onDisable(false)

      if (['card_error', 'validation_error'].includes(result.error.type) && result.error.message) {
        props.onErrorText(result.error.message)
      } else {
        props.onErrorText(t('An error occured processing your credit card'))
      }
    }).catch(() => {
      // This should never happen according to Stripe, but we still handle it
      props.onDisable(false)
      props.onErrorText(t('An error occured processing your credit card'))
    })
  }, [props.orders, props.savePaymentMethod, props.confirmationUrl, stripe, elements, t, props.onDisable, props.onErrorText])

  const payWithCard = useCallback(() => {
    if (!stripe) {
      return
    }

    props.onDisable(true)
    props.onErrorText('')

    // Provide id order if we're paying an 'ordered' order
    const firstOrder = props.orders.count() === 1 ? props.orders.first() : undefined
    const idOrder = firstOrder?.get('status') === 'ordered' ? firstOrder.get('id') : undefined

    props.orders.forEach(cart => {
      const eventId = getMetaCheckoutEventId('AddPaymentInfo', cart, 'stripe-saved-card')
      trackMetaBrowserEvent('AddPaymentInfo', cart, eventId)
      queueMetaServerEvent('AddPaymentInfo', cart, eventId, 'stripe')
      trackOpenAIAdsPaymentInfoAdded(cart, eventId)
      queueOpenAIAdsServerEvent('payment_info_added', cart, eventId)
    })

    void dispatch(CartCheckoutStripeWithCard(props.selectedCreditCard, idOrder, props.confirmationUrl).set({
      onSuccess: (response) => {
        if (response.data.secret) {
          void stripe.confirmPayment({
            clientSecret: response.data.secret,
            confirmParams: {
              return_url: window.SITE_URL + 'checkout/creditcard-v2/payment-intent-callback?saveCard=0&redirect_confirm=' + encodeURIComponent(props.confirmationUrl)
            },
            redirect: 'always'
          }).then((result) => {
            props.onDisable(false)
            props.onErrorText(result.error.message ?? t('An error occured processing your credit card'))
          }).catch(() => {
            props.onDisable(false)
            props.onErrorText(t('An error occured processing your credit card'))
          })
          return
        }

        void navigate(props.confirmationUrl + '?payment_id=' + encodeURIComponent(response.data.payment_id))
      },
      onFailure: (response) => {
        props.onDisable(false)

        if (response.data?.error === 'invalid-credit-card') {
          props.onErrorText(t('This saved credit card could not be used. Please add it again or choose another payment method.'))
        } else if (response.data?.error === 'credit-card-server-error') {
          props.onErrorText(t('An error occured processing your credit card. Please try again later.'))
        } else if (response.data?.error) {
          props.onErrorText(response.data.error)
        } else {
          props.onErrorText(t('An error occured processing your credit card. Please try again later.'))
        }
      }
    }))
  }, [dispatch, navigate, stripe, props.orders, props.selectedCreditCard, props.confirmationUrl, props.onErrorText, props.onDisable, t])

  return <Button
    onClick={props.selectedCreditCard > 0 ? payWithCard : payWithIntent}
    {...otherProps}
  />
}
